import { useEffect } from 'react'
import { useQueryClient, type QueryClient } from '@tanstack/react-query'
import { getSupabase } from '../api/supabase'

/**
 * 订阅 supabase auth 状态变化,当认证身份(user id)改变时清空整个 react-query 缓存。
 *
 * 根因(换号文件列表残留 bug):
 *   QueryClient 是模块级全局,跨登录 / 登出 / 换号存活;且 user 维度的 query key
 *   (如 ['file','list',parentId])不含 user id。换号后新账号会命中上一账号的缓存,
 *   在 staleTime 内不重新拉取 —— 整页刷新之所以"恢复正常",仅因为它重建了一个空的
 *   QueryClient。这同时是跨账号数据泄露。
 *
 * 修复策略(在源头一次性解决,而非逐个 query key 加 user id):
 *   - 身份切换(A→B)或登出(→null):qc.clear() 清空全部缓存
 *   - 同一用户 token 刷新 / tab 重聚焦(相同 id):不清空,避免无谓 refetch 风暴
 *   - 首次观测(INITIAL_SESSION):仅记录身份,不清空
 *
 * 返回取消订阅函数。
 */
export function subscribeAuthCacheReset(qc: QueryClient): () => void {
  // undefined = 尚未观测到首个 auth 事件;null = 未登录;string = 已登录用户 id
  let prevUserId: string | null | undefined = undefined

  const { data } = getSupabase().auth.onAuthStateChange((_event, session) => {
    const nextUserId = session?.user?.id ?? null

    if (prevUserId === undefined) {
      // 首次观测(通常是 INITIAL_SESSION):仅记录基线身份,不清空。
      prevUserId = nextUserId
      return
    }

    if (prevUserId !== nextUserId) {
      prevUserId = nextUserId
      qc.clear()
    }
  })

  return () => data.subscription.unsubscribe()
}

/**
 * 挂载于 QueryClientProvider 内部的零渲染组件:身份变化时清空缓存。
 * 各 SPA 在 <QueryClientProvider> 内渲染一次 <AuthCacheReset /> 即可。
 */
export function AuthCacheReset(): null {
  const qc = useQueryClient()
  useEffect(() => subscribeAuthCacheReset(qc), [qc])
  return null
}
